import { ColumnDef, createColumnHelper } from '@tanstack/react-table'

import { DefinitionListItem } from 'src/components/atoms/DefinitionListItem'
import { ButtonLink, InternalLink } from 'src/components/atoms/Link'
import { Table } from 'src/components/molecules/Table'

import {
  applicationPlantingReportPath,
  createPlantingPath,
  viewApplicationPath,
  viewProjectDetailPath
} from 'src/config/paths'
import useMediaQuery from 'src/hooks/useMediaQuery'
import {
  ApplicationWorkflowState,
  ListDashboardApplicationType
} from 'src/types/application'

export interface DashboardApplicationsTableProps {
  applicationsData: ListDashboardApplicationType[]
}

export const DashboardApplicationsTable = ({
  applicationsData
}: DashboardApplicationsTableProps) => {
  const getActionCtaProps = (
    applicationId: string,
    state: ApplicationWorkflowState
  ) => {
    switch (state) {
      case ApplicationWorkflowState.Approved:
        return {
          title: 'Report planting',
          href: createPlantingPath({
            routeParams: {
              applicationId
            }
          })
        }
      case ApplicationWorkflowState.Planted:
        return {
          title: 'Report monitoring',
          href: applicationPlantingReportPath({
            routeParams: {
              applicationId
            }
          })
        }
      default:
        return {
          title: 'View',
          href: viewApplicationPath({
            routeParams: { applicationId }
          })
        }
    }
  }

  const columnHelper = createColumnHelper<ListDashboardApplicationType>()
  const mobileColumns: ColumnDef<ListDashboardApplicationType, any>[] = [
    columnHelper.accessor('Id', {
      cell: ({ row: { original } }) => {
        const ctaProps = getActionCtaProps(
          original.Id,
          original.WorkflowState as ApplicationWorkflowState
        )
        return (
          <div className='flex flex-col'>
            <dl className='mb-3 pb-2'>
              <DefinitionListItem title='Season' data={original.Season?.Name} />
              <DefinitionListItem
                title='Project'
                data={
                  original.Project?.Id ? (
                    <InternalLink
                      className='line-clamp-2'
                      href={viewProjectDetailPath({
                        routeParams: {
                          projectId: original.Project.Id
                        }
                      })}
                    >
                      {original.Project.Name}
                    </InternalLink>
                  ) : (
                    '-'
                  )
                }
              />
              <DefinitionListItem
                title='Request reference'
                data={original.Reference}
              />
              <DefinitionListItem
                title='Status'
                data={original.WorkflowState}
              />
              <DefinitionListItem
                title='Applied for'
                data={original.Required}
              />
              <DefinitionListItem title='Approved' data={original.Approved} />
            </dl>
            <ButtonLink
              variant='contained'
              color='tertiary'
              href={ctaProps.href}
            >
              {ctaProps.title}
            </ButtonLink>
          </div>
        )
      }
    })
  ]

  const desktopColumns: ColumnDef<ListDashboardApplicationType, any>[] = [
    columnHelper.accessor('Season.Name', {
      size: 120,
      cell: props => {
        return <span>{props.getValue()}</span>
      },
      header: 'Season'
    }),
    columnHelper.accessor('Project.Id', {
      header: 'Project',
      cell: ({ row: { original } }) =>
        original.Project?.Id ? (
          <InternalLink
            className='line-clamp-2'
            href={viewProjectDetailPath({
              routeParams: {
                projectId: original.Project.Id
              }
            })}
          >
            {original.Project.Name}
          </InternalLink>
        ) : (
          '-'
        )
    }),
    columnHelper.accessor('Reference', {
      header: 'Request reference',
      size: 100,
      cell: props => <span className='font-bold'>{props.getValue()}</span>
    }),
    columnHelper.accessor('WorkflowState', {
      size: 130,
      cell: props => <span>{props.getValue()}</span>,
      header: 'Status'
    }),
    columnHelper.accessor('Required', {
      cell: props => <span>{props.getValue()}</span>,
      header: 'Applied for'
    }),
    columnHelper.accessor('Approved', {
      cell: props => <span>{props.getValue()}</span>,
      header: 'Approved'
    }),
    columnHelper.accessor('Id', {
      size: 220,
      cell: props => {
        const ctaProps = getActionCtaProps(
          props.row.original.Id,
          props.row.original.WorkflowState as ApplicationWorkflowState
        )
        return (
          <ButtonLink
            className='w-full'
            variant='contained'
            color='tertiary'
            href={ctaProps.href}
          >
            {ctaProps.title}
          </ButtonLink>
        )
      },
      header: 'Action'
    })
  ]

  const isMobile = useMediaQuery('(max-width: 1024px)')

  const columns = isMobile ? mobileColumns : desktopColumns

  return (
    <Table
      className='[&>thead]:sr-only lg:[&>thead]:not-sr-only'
      columns={columns}
      data={applicationsData || []}
      headerClasses='!text-left px-2 !pt-0 !pb-4'
      cellClasses='!text-left px-2'
      enableSorting={false}
      noResultsText="You haven't requested trees yet. Once you do, you'll be able to view and manage requests here."
    />
  )
}
